import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, MapPin, User, Target } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Training } from '@/types/trainings';
import { useTrainingPartners, useTrainingPartnersFromData } from '@/hooks/useTrainingPartners';
import { useAuth } from '@/contexts/AuthContext';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { ToggleSelector } from '@/components/ui/ToggleSelector';
import { DeleteConfirmationModal } from '@/components/ui/DeleteConfirmationModal';
import { useDeleteTraining } from '@/hooks/useDeleteTraining';
import { useToast } from '@/hooks/use-toast';
import { useTrainingInstructor } from '@/hooks/useTrainingInstructor';
import { useI18n } from '@/contexts/I18nContext';
import { formatDateTimeForDisplay } from '@/utils/timezoneUtils';

interface TrainingViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  training: Training | null;
  notesData?: any;
  onStatusUpdate?: (trainingId: string, status: 'Completed' | 'Missed') => void;
  onOpenNotesModal?: (training: Training) => void;
  onEditTraining?: (training: Training) => void;
  onTrainingDeleted?: () => void;
  onTrainingUpdated?: () => void;
  refreshKey?: number;
}

interface TrainingInstructor {
  id: string;
  instructor_user_id?: string;
  name?: string;
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
  manual_name?: string;
  is_manual: boolean;
  created_at: string;
}

export const TrainingViewModal = forwardRef<
  { forceReload: () => Promise<void>; forceReloadInstructor: () => Promise<void> },
  TrainingViewModalProps
>(({ isOpen, onClose, training, notesData: propNotesData, onStatusUpdate, onOpenNotesModal, onEditTraining, onTrainingDeleted, onTrainingUpdated, refreshKey = 0 }, ref) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInstructorLoading, setIsInstructorLoading] = useState(false);
  const [instructorRefreshKey, setInstructorRefreshKey] = useState(0);
  
  // Add refetch state for partners
  const [isRefetchingPartners, setIsRefetchingPartners] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  // Use the training instructor hook with refresh key
  const {
    instructor,
    loading: instructorLoading,
    reloadInstructor
  } = useTrainingInstructor({ training, refreshKey: instructorRefreshKey });

  // Get training notes data from props
  const trainingNotesData = propNotesData?.[training?.id || ''];
  const notesData = trainingNotesData;
  const notesLoading = !trainingNotesData && training?.status === 'Completed';

  // Use partners from training data if available, otherwise from API
  const { partners: apiPartners, loading: partnersLoading } = useTrainingPartnersFromData(
    training?.id || '',
    [], // Empty array since partners are now loaded from API
    refreshKey // Pass refreshKey to trigger reload when needed
  );
  
  // Use training partners if available, otherwise fallback to API
  const partners = training?.partners && training.partners.length > 0 ? training.partners : apiPartners;
  const partnersLoadingState = partnersLoading && !training?.partners?.length;

  // Functions to translate training types and categories
  const getLocalizedTrainingType = (type: string) => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  const getLocalizedTrainingCategory = (category: string) => {
    // Convert category to lowercase and replace spaces with underscores for translation key
    const categoryKey = category.toLowerCase().replace(/\s+/g, '_');
    return t(`calendar.training_categories.${categoryKey}`) || category;
  };

  const getLocalizedTechniqueName = (techniqueName: string) => {
    // Map database technique names to simplified translation keys
    const techniqueMapping: { [key: string]: string } = {
      // Guard Types
      'Closed Guard': 'closed_guard',
      'Open Guard': 'open_guard',
      'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)': 'open_guard',
      'Half Guard': 'half_guard',
      'Butterfly Guard': 'butterfly_guard',
      'Worm Guard': 'worm_guard',
      'Reverse De La Riva Guard': 'reverse_de_la_riva_guard',
      '50/50 Guard': '50_50_guard',
      'Sitting Guard': 'sitting_guard',
      'No-Gi Specific Guards': 'no_gi_specific_guards',
      
      // Guard Passing
      'Pressure Passing': 'pressure_passing',
      'Torque/Leg Drag Passing': 'torque_leg_drag_passing',
      'Leg Weave Passing': 'leg_weave_passing',
      'Floating/Smash Passing': 'floating_smash_passing',
      'Movement-Based Passing': 'movement_based_passing',
      'Folding Passes': 'folding_passes',
      'No-Gi Specific Passes': 'no_gi_specific_passes',
      
      // Submissions
      'Armlocks': 'armlocks',
      'Chokes': 'chokes',
      'Leg Locks': 'leg_locks',
      'Shoulder Locks': 'shoulder_locks',
      'Spine/Neck Cranks': 'spine_neck_cranks',
      'No-Gi Specific Submissions': 'no_gi_specific_submissions',
      
      // Sweeps & Takedowns
      'Guard Sweeps': 'guard_sweeps',
      'Half Guard Sweeps': 'half_guard_sweeps',
      'Butterfly Sweeps': 'butterfly_sweeps',
      'De La Riva Sweeps': 'de_la_riva_sweeps',
      'Takedowns from Standing': 'takedowns_from_standing',
      'Counter Takedowns': 'counter_takedowns',
      'Pulling Guard': 'pulling_guard',
      
      // Escapes & Reversals
      'Mount Escapes': 'mount_escapes',
      'Back Escapes': 'back_escapes',
      'Side Control Escapes': 'side_control_escapes',
      'North South Escapes': 'north_south_escapes',
      'Submission Escapes': 'submission_escapes',
      'Turtle Escapes': 'turtle_escapes',
      'Half Guard Escapes': 'half_guard_escapes'
    };

    // Try to find the mapped key
    const mappedKey = techniqueMapping[techniqueName];
    if (mappedKey) {
      return t(`calendar.techniques.${mappedKey}`) || techniqueName;
    }

    // Fallback: try to convert the name directly (for any new techniques)
    const techniqueKey = techniqueName.toLowerCase().replace(/\s+/g, '_');
    return t(`calendar.techniques.${techniqueKey}`) || techniqueName;
  };

  // Format date according to language
  const formatLocalizedDate = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "24 de agosto de 2025"
      const month = date.toLocaleDateString('pt-BR', { month: 'long' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day} de ${month} de ${year}`;
    } else {
      // English format: "August 24, 2025"
      return format(date, 'MMMM d, yyyy');
    }
  };

  // Format time according to language
  const formatLocalizedTime = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "14:00 às 15:00"
      return format(date, 'HH:mm');
    } else {
      // English format: "2:00 PM"
      return format(date, 'h:mm a');
    }
  };
  

  // Force reload function exposed via ref
  const forceReload = useCallback(async () => {
    if (!training?.id) return;
    
    setIsRefetchingPartners(true);
    
    try {
      // Force refetch by updating the key
      setRefetchKey(prev => prev + 1);
      
      // Simulate a small delay to show shimmer
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error) {
      console.error('🔄 TrainingViewModal - Error reloading data:', error);
    } finally {
      setIsRefetchingPartners(false);
    }
  }, [training?.id]);

  // Force reload instructor with shimmer
  const forceReloadInstructor = useCallback(async () => {
    if (!training?.id) return;
    
    setIsInstructorLoading(true);
    
    try {
      // Force refresh by updating the key
      setInstructorRefreshKey(prev => prev + 1);
      
      // Small delay to show shimmer
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error reloading instructor:', error);
    } finally {
      setIsInstructorLoading(false);
    }
  }, [training?.id]);

  // Force refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0 && training?.id) {
      console.log('🔄 TrainingViewModal - refreshKey changed:', refreshKey);
      setInstructorRefreshKey(prev => prev + 1);
      setRefetchKey(prev => prev + 1);
    }
  }, [refreshKey, training?.id]);

  // Force refresh when modal opens or training changes
  useEffect(() => {
    if (isOpen && training?.id) {
      // Force refresh by updating the key
      setInstructorRefreshKey(prev => prev + 1);
    }
  }, [isOpen, training?.id]);

  // Force refresh when training data changes
  useEffect(() => {
    if (training?.id) {
      // Small delay to ensure data is updated
      setTimeout(() => {
        setInstructorRefreshKey(prev => prev + 1);
      }, 100);
    }
  }, [training?.id]);

  // Expose forceReload via ref
  useImperativeHandle(ref, () => ({
    forceReload,
    forceReloadInstructor
  }), [forceReload, forceReloadInstructor]);

  // Force update when notesData changes
  useEffect(() => {
    if (training?.id && notesData) {
      // Force a reload to show updated data
      setRefetchKey(prev => prev + 1);
    }
  }, [training?.id, notesData]);

  // TODO: Add proper user authentication check
  const canEditTraining = true; // Temporarily allow editing for all trainings

  // More robust user ID comparison
  const canDeleteTraining = training?.user_id && user?.id && training.user_id === user.id;

  // Temporary override for testing - uncomment to test delete functionality
  // const canDeleteTraining = true;

  // Update local status when training prop changes
  useEffect(() => {
    if (training) {
      setCurrentStatus(training.status);
      
      // Force notes refetch by incrementing refresh key
      setNotesRefreshKey(prev => prev + 1);
    }
  }, [training?.id, training?.status]);

  // Refetch instructor when modal opens
  useEffect(() => {
    if (isOpen && training?.id) {
      reloadInstructor();
    }
  }, [isOpen, training?.id, reloadInstructor]);

  const isPastTraining = () => {
    if (!training) return false;
    return new Date(training.end_time) < new Date();
  };

  const getStatusColor = () => {
    switch (currentStatus) {
      case 'Completed':
        return 'border-l-green-500';
      case 'Missed':
        return 'border-l-red-500';
      default:
        return 'border-l-blue-500';
    }
  };

  const getStatusTextColor = () => {
    switch (currentStatus) {
      case 'Completed':
        return 'text-green-600';
      case 'Missed':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const handleStatusUpdate = async (newStatus: 'Completed' | 'Missed') => {
    if (!training || !onStatusUpdate) return;

    setIsUpdatingStatus(true);
    try {
      await onStatusUpdate(training.id, newStatus);
      setCurrentStatus(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleOpenNotes = () => {
    if (onOpenNotesModal && training) {
      onOpenNotesModal(training);
      // Add a callback to refresh training data when notes modal closes
      const checkForUpdates = () => {
        // Force a refresh of the training data
        if (training) {
          // This will trigger a re-render with updated data
          setNotesRefreshKey(prev => prev + 1);
        }
      };

      // Set up a listener for when the notes modal closes
      setTimeout(checkForUpdates, 100);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const { deleteTraining, isDeleting: isDeletingFromHook } = useDeleteTraining();

  const handleDeleteConfirm = async () => {
    if (!training) return;

    const result = await deleteTraining(training.id);

    if (result.success) {
      toast({
        title: t('common.success'),
        description: t('calendar.training_deleted_successfully'),
        duration: 3000,
      });
      setIsDeleteModalOpen(false);
      onTrainingDeleted?.(); // Call the callback to refresh the calendar
    } else {
      toast({
        title: t('common.error'),
        description: result.error || t('calendar.failed_to_delete_training'),
        duration: 5000,
      });
    }
  };

  const renderTechniques = (techniques: any[]) => {
    if (!techniques || techniques.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {techniques.map((technique, index) => {
          // Handle different data structures
          let techniqueName = '';
          if (typeof technique === 'string') {
            techniqueName = technique;
          } else if (technique?.name) {
            techniqueName = technique.name;
          } else if (technique?.id) {
            // Try to get name from technique ID
            techniqueName = `Technique ${technique.id}`;
          } else {
            techniqueName = `Technique ${index + 1}`;
          }
          
          return (
            <Badge key={index} variant="secondary" className="text-xs">
              {techniqueName}
            </Badge>
          );
        })}
      </div>
    );
  };

  const renderNotesPreview = () => {
    if (!notesData?.notes?.full_notes) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-500">{t('calendar.no_notes_added_yet')}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenNotes}
            className="text-xs"
          >
            {t('calendar.add_notes')}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-black">{t('calendar.training_notes')}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenNotes}
            className="text-xs"
          >
            {t('calendar.view_notes')}
          </Button>
        </div>
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          {notesData.notes.full_notes || notesData.notes.general_notes || t('calendar.no_detailed_notes_available')}
        </div>
      </div>
    );
  };

  const shouldShowActionButtons = () => {
    if (!training) return false;

    // Show action buttons for past scheduled trainings OR for {t('calendar.completed')}/{t('calendar.missed')} trainings
    return isPastTraining() ||
           (training.status === 'Completed' || training.status === 'Missed');
  };

  if (!training) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl p-6">
        <DialogTitle className="sr-only">{t('calendar.training_details')}</DialogTitle>
        <DialogDescription id="training-details-description" className="sr-only">
          {t('calendar.training_details_description')}
        </DialogDescription>
        <SossietyDialogHeader
          title={t('calendar.training_details')}
          titleClassName="text-lg"
        >
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        {/* Status Selector Buttons - Only show for past trainings */}
        {isPastTraining() && (
          <ToggleSelector
            label="Status"
            options={[
              { value: 'Completed', label: t('calendar.completed'), color: 'green' },
              { value: 'Missed', label: t('calendar.missed'), color: 'red' }
            ]}
            value={currentStatus}
            onChange={(value) => handleStatusUpdate(value as 'Completed' | 'Missed')}
            disabled={isUpdatingStatus}
            className="mb-2"
          />
        )}

        <div className="space-y-2 max-h-[75vh] overflow-y-auto pr-2">
          {/* Training Type and Modality */}
          <div className="flex items-center justify-between">
            <span className="text-black font-semibold" style={{ fontSize: '16px', fontWeight: 600 }}>
              {training.type ? getLocalizedTrainingType(training.type) : t('calendar.group_class')} - {training.modality}
            </span>
          </div>

          {/* Training Category */}
          {training.category && (
            <div className="!mt-0">
              <span className="text-gray-600 font-medium text-sm">
                {getLocalizedTrainingCategory(training.category)}
              </span>
            </div>
          )}

          {/* Date and Time */}
          <div className="!mt-0 !mb-3">
            <p className="text-gray-500 font-sans text-xs">
              {formatLocalizedDate(new Date(training.start_time))} - {formatDateTimeForDisplay(training.start_time).time} {currentLanguage === 'pt' ? 'às' : 'to'} {formatDateTimeForDisplay(training.end_time).time}
            </p>
          </div>


          {/* Location */}
          <div className="!mb-3">
            <label className="block text-sm font-semibold text-black">{t('profile.location')}</label>
            <p className="text-sm text-gray-500">
              {training.academy_name || t('calendar.no_location_specified')}
            </p>
          </div>

          {/* Instructor Section */}
          <div className="space-y-2 !mb-3">
            <div className="flex items-center">
              <h3 className="block text-sm font-semibold text-gray-900">{t('profile.instructor')}</h3>
            </div>
            
            {(instructorLoading || isInstructorLoading) ? (
              <div className="animate-pulse">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ) : instructor ? (
              <InstructorDisplay
                instructor={instructor}
                size="md"
                className="rounded-lg"
              />
            ) : (
              <div className="text-gray-500 text-sm rounded-lg">
                {t('calendar.no_instructor_assigned')}
              </div>
            )}
          </div>
          
          {/* Training Notes Section */}
          {currentStatus === 'Completed' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-black font-semibold" style={{ fontSize: '16px', fontWeight: 600 }}>
                  {t('calendar.training_notes')}
                </h3>
                <button
                  onClick={handleOpenNotes}
                  className="text-xs text-[#D72E16] hover:text-[#B8251A] font-medium underline"
                >
                  {(() => {
                    const hasNotes = !!(
                      notesData?.notes?.full_notes || 
                      notesData?.notes?.general_notes || 
                      notesData?.notes?.overall_rating ||
                      notesData?.techniques?.length > 0 ||
                      notesData?.partners?.length > 0
                    );
                    return hasNotes ? t('calendar.view_notes') : t('calendar.add_notes');
                  })()}
                </button>
              </div>

              {notesLoading ? (
                <div className="space-y-3">
                  {/* Rating Shimmer */}
                  <div className="flex items-center space-x-2">
                    <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"></div>
                      ))}
                    </div>
                  </div>

                  {/* Techniques Shimmer */}
                  <div>
                    <div className="w-20 h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="flex flex-wrap gap-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-16 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                      ))}
                    </div>
                  </div>

                  {/* Notes Shimmer */}
                  <div>
                    <div className="w-12 h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="space-y-2">
                      <div className="w-full h-3 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-3/4 h-3 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-1/2 h-3 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ) : notesData ? (
                <div className="space-y-3">
                  {/* Techniques */}
                  {notesData.techniques && notesData.techniques.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('calendar.techniques_label')} </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {renderTechniques(notesData.techniques)}
                      </div>
                    </div>
                  )}

                  {/* Overall Rating */}
                  <div className="space-y-3 !mt-8">
                    {(() => {
                      const shouldShowRating = notesData.notes;
                      const ratingValue = notesData.notes?.overall_rating;
                      const hasRating = ratingValue !== null && ratingValue !== undefined;
                      
                      return shouldShowRating ? (
                        <div>
                          <span className="text-sm font-medium text-gray-700">{t('calendar.overall_rating')}: </span>
                          {hasRating ? (
                            <div className="flex space-x-1 mt-1">
                              {[1, 2, 3, 4, 5].map((rating) => {
                                const isFilled = rating <= ratingValue;
                                return (
                                  <div
                                    key={rating}
                                    className={`w-3 h-3 rounded-full ${
                                      isFilled
                                        ? 'bg-[#D72E16]'
                                        : 'bg-gray-200'
                                    }`}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 mt-1">{t('calendar.no_rating')}</span>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Notes */}
                  {notesData.notes?.full_notes && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('calendar.notes_label')} </span>
                      <p className="text-sm text-gray-900 mt-1 line-clamp-3">
                        {notesData.notes.full_notes}
                      </p>
                    </div>
                  )}

                  {/* Students/Training Partners (now part of notes) */}
                  {notesData.notes && (
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          {training.is_class ? t('calendar.students') : t('calendar.training_partners_label')} 
                        </span>
                      </div>
                      
                      {partnersLoadingState ? (
                        <div className="space-y-2 pt-2">
                          {/* Partners/Students Shimmer */}
                          <div className="flex items-center space-x-3 p-3 rounded-lg">
                            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="flex-1 space-y-1">
                              <div className="w-24 h-3 bg-gray-200 rounded animate-pulse"></div>
                              <div className="w-16 h-2 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      ) : partners && partners.length > 0 ? (
                        <div className="">
                          {partners.map((partner) => (
                            <div key={partner.id} className="mt-2 mb-2">
                              <PartnerDisplay
                                key={partner.id}
                                partner={partner}
                                showRemoveButton={false}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center rounded-lg">
                          <span className="text-sm text-gray-900">
                            {training.is_class ? t('calendar.no_students_registered') : t('calendar.no_training_partners_added')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('calendar.no_notes_added_yet_click')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {canDeleteTraining && (
            <Button
              onClick={handleDeleteClick}
              disabled={false}
              variant="outline"
              className="flex-1 border-black text-black hover:bg-gray-50 rounded-full"
            >
              {t('common.delete')}
            </Button>
          )}
          {canEditTraining && onEditTraining && (
            <Button
              onClick={() => onEditTraining(training)}
              className="flex-1 bg-[#D72E16] hover:bg-[#B8251A] text-white rounded-full"
            >
              {t('common.edit')}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete') + ' ' + t('calendar.training').toLowerCase()}
        message={t('calendar.delete_training_confirmation')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeletingFromHook}
      />
    </Dialog>
  );
}); 